<template>
	<div class="unoapp-typeahead" :class="{'open': showDropdown}">

		<!-- Predictive Input (Readonly) -->
		<input type="text" class="form-control prediction-input" readonly autocomplete="off" spellcheck="false" tabindex="-1" v-model="predictiveValue"/>

		<!-- Typeahead Input -->
		<input type="text" class="form-control input-sm prediction-output-input" :class="{'edited': value.length}" :placeholder="placeholder" autocomplete="off" v-model="value" @input="update" @keydown.up="up" @keydown.down="down" @keydown.enter="hit" @keydown.esc="reset" @keydown.tab="selectPredictiveOption" @blur="showDropdown = false; hidePredictiveValue()" @focus="onFocus()" :tabindex="tabindex"/>

		<!-- DROPDOWN START -->
		<ul class="dropdown-menu">
			<li v-for="(item, index) in items" :class="{'active': isActive(index)}">
				<a @mousedown.prevent="hit" @mousemove="setActive(index)">
					<span v-if="multiSearch" v-html="highlight(buildText(item))"></span>
					<span v-if="!objectKey && !multiSearch" v-html="highlight(item)"></span>
					<span v-if="objectKey && !multiSearch" v-html="highlight(item[objectKey])"></span>
				</a>
			</li>
			<li v-if="!items.length">
				<a>
					<span>There are no {{ `${label.toLowerCase()}s` }} that match your search.</span>
				</a>
			</li>
		</ul>
		<!-- DROPDOWN END -->
		
		<label>{{ label }}</label>
		<span class="help-block persist" v-if="helpBlock" v-html="helpBlock"></span>
	</div>
</template>

<script>
/**
 * Typeahead module is used to build a front end search through data.
 * @class Typeahead
 * @prop {array} data - Used to pass the list to be searched through from the parent
 * @prop {string} emitter - Used to determine what to emit to once an option is selected
 * @prop {string} filter - Used to determine if there are items to emit when searching through the data
 * @prop {string} helpBlock - Used to determine if the help-block should be shown and what the text should be
 * @prop {string} label - Used to determine the label for the typeahead
 * @prop {integer} limit - Used to determine the max amount fo results to show
 * @prop {string} objectKey - Used to determine if we should be searching for a specific key within an object when matching results
 * @prop {string} placeholder - Used to determine the placeholder text for the typeahead
 * @prop {string} passedValue - Used to determine if there is an initial value to set
 * @prop {integer} tabindex - Used to determine if the typeahead should be able to be tabbed into
 * @prop {boolean} onFirstFocus - Used to determine if the element has been focused on for the first time
 * @since 0.0.4
 */

import $ from 'jquery'
import { isEmpty } from 'lodash'

export default {
	name: 'typeahead',
	props: {
		data: {
			type: Array,
			required: true,
			default: () => { return [] }
		},
		emitter: {
			type: String
		},
		filter: {},
		helpBlock: {
			type: String
		},
		label: {
			default: '',
			type: String
		},
		limit: {
			type: Number,
			default: 8
		},
		objectKey: {
			type: String,
			default: null
		},
		placeholder: {
			type: String
		},
		passedValue: {
			default: null
		},
		tabindex: {
			default: 0
		},
		onFirstFocus: {
			default: false
		},
		multiSearch: {
			default: false
		},
		multiSearchKeys: {
			default: () => { return [] }
		}
	},
	data () {
		return {
			showDropdown: false,
			noResult: true,
			current: 0,
			items: [],
			value: '',
			predictiveValue: ''
		}
	},
	/**
	 * Run on `created` to initialize the items list and to set the initial value if one was passed into the component.
	 * @function
	 * @returns {undefined}
	 * @memberof Typeahead
	 * @version 0.0.7
	 */
	created () {
		this.items = this.primitiveData
		this.runFilter()
		if (this.passedValue) {
			if (this.multiSearch) {
				if (!isEmpty(this.passedValue)) {
					this.value = this.buildText(this.passedValue)
				}
			} else if (this.objectKey) {
				this.value = this.passedValue[this.objectKey]
			} else {
				this.value = this.passedValue
			}
		}
	},
	computed: {
		/**
		 * Compute data that matches the current search in the typeahead.
		 * @function
		 * @returns {array} - An array of objects that meet the search criteria.
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		primitiveData () {
			if (this.data.length) {
				return this.data.filter(value => {
					if (!this.value.length && this.onFirstFocus) {
						return this.data.slice(0, this.limit)
					}
					const query = this.value.toLowerCase()
					let isValueTrue = false
					if (this.multiSearch) {
						for (let i = 0, x = this.multiSearchKeys.length; i < x; i++) {
							if (value[this.multiSearchKeys[i].parent_key]) {
								if (value[this.multiSearchKeys[i].parent_key][this.multiSearchKeys[i].key] && value[this.multiSearchKeys[i].parent_key][this.multiSearchKeys[i].key].toLowerCase().indexOf(query) !== -1) {
									isValueTrue = true
								}
							} else if (value[this.multiSearchKeys[i].key] && value[this.multiSearchKeys[i].key].toLowerCase().indexOf(query) !== -1) {
								isValueTrue = true
							}
						}
					} else if (this.objectKey) {
						value = value[this.objectKey].toLowerCase()
						if (value.indexOf(query) !== -1) {
							isValueTrue = true
						}
					} else {
						value = value.toLowerCase()
						if (value.indexOf(query) !== -1) {
							isValueTrue = true
						}
					}
					return isValueTrue
				}).slice(0, this.limit)
			} else {
				return []
			}
		},
		/**
		 * Compute an alphabetically sorted list for use in predictions
		 * @function
		 * @returns {array} - A sorted version of the items array.
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		sortedPredictionList () {
			return this.items.sort((a, b) => {
				if (this.multiSearch) {
					/**
					 * Always just sort by the first key value
					 * Others won't be used for predictions since it's probably a less common use case and really complicates everything.
					 */
					if (this.multiSearchKeys[0].parent_key) {
						if (a[this.multiSearchKeys[0].parent_key][this.multiSearchKeys[0].key] < b[this.multiSearchKeys[0].parent_key][this.multiSearchKeys[0].key]) return -1
						if (a[this.multiSearchKeys[0].parent_key][this.multiSearchKeys[0].key] > b[this.multiSearchKeys[0].parent_key][this.multiSearchKeys[0].key]) return 1
					} else {
						if (a[this.multiSearchKeys[0].key] < b[this.multiSearchKeys[0].key]) return -1
						if (a[this.multiSearchKeys[0].key] > b[this.multiSearchKeys[0].key]) return 1
					}
				} else if (this.objectKey) {
					if (a[this.objectKey] < b[this.objectKey]) return -1
					if (a[this.objectKey] > b[this.objectKey]) return 1
				} else {
					if (a < b) return -1
					if (a > b) return 1
				}
				return 0
			})
		}
	},
	methods: {
		/**
		 * To update the items list, show the dropdown, and updated the current prediction. Called whenever the input changes.
		 * @function
		 * @returns {boolean} - Returns only if there is no value set.
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		update () {
			if (!this.value) {
				this.reset()
				this.$emit(this.emitter, '')
				return false
			}

			if (this.data) {
				this.items = this.primitiveData
				this.runFilter()
				this.showDropdown = true
				this.calculatePredictiveValue()
			}
		},
		/**
		 * To reset the items list and clear the current value.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		reset () {
			if (this.onFirstFocus) {
				this.items = this.data.slice(0, this.limit)
				this.runFilter()
				this.showDropdown = true
			} else {
				this.items = []
				this.showDropdown = false
			}
			this.value = ''
		},
		/**
		 * To update the current item. The current item is used to set a value when selected.
		 * @function
		 * @param {integer} index - the index within the items array that is to be set as current.
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		setActive (index) {
			this.current = index
		},
		/**
		 * To compare the current index to a passed index to check if it is the currently active index.
		 * @function
		 * @param {integer} index - the index within the items array that is to be checked.
		 * @returns {boolean} - Returns whether the item passed in is the current active item.
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		isActive (index) {
			return this.current === index
		},
		/**
		 * To set the current item (or the override) as the active item and pass it to the parent.
		 * @function
		 * @param {event} e - the event that triggered the function.
		 * @param {integer} override - the index that is to be set to override the current index.
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		hit (e, override) {
			this.predictiveValue = ''
			if (e) {
				e.preventDefault()
			}

			let currentOverride = this.current
			if (override || override === 0) {
				currentOverride = override
			}

			if (this.items[currentOverride]) {
				this.showDropdown = false
				if (this.multiSearch) {
					this.value = this.buildText(this.items[currentOverride])
					this.$emit(this.emitter, this.items[currentOverride])
				} else if (this.objectKey) {
					this.value = this.items[currentOverride][this.objectKey]
					this.$emit(this.emitter, this.items[currentOverride])
				} else {
					this.value = this.items[currentOverride]
					this.$emit(this.emitter, this.value)
				}
			}
		},
		/**
		 * To move the current index up the dropdown by one index.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		up () {
			if (this.current > 0) {
				this.current--
			}
		},
		/**
		 * To move the current index down the dropdown by one index.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		down () {
			if (this.current < this.items.length - 1) {
				this.current++
			}
		},
		/**
		 * To let the parent know that the input has been focused on so that they can perform an event if needed.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		onFocus () {
			this.$emit('onFocus')
			$(this.$el).find('.prediction-output-input').select()
			if (this.onFirstFocus) {
				this.showDropdown = true
			}
		},
		/**
		 * To build a sentence based on the multiSearchKeys passed in.
		 * @function
		 * @param {object} item - The item being used to create the sentence
		 * @returns {string} The sentence that was created.
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		buildText (item) {
			let text = ''
			for (let i = 0, x = this.multiSearchKeys.length; i < x; i++) {
				if (item[this.multiSearchKeys[i].parent_key]) {
					if (this.multiSearchKeys[i].sub_string && text.length) {
						text += '- '
					}
					if (item[this.multiSearchKeys[i].parent_key][this.multiSearchKeys[i].key].length) {
						text += item[this.multiSearchKeys[i].parent_key][this.multiSearchKeys[i].key]
						if (i + 1 !== x) {
							text += ' '
						}
					}
				} else {
					if (this.multiSearchKeys[i].sub_string && text.length) {
						text += '- '
					}
					if (item[this.multiSearchKeys[i].key].length) {
						text += item[this.multiSearchKeys[i].key]
						if (i + 1 !== x) {
							text += ' '
						}
					}
				}
			}
			return text
		},
		/**
		 * To highlight the characters in the passed in phrase that match the input value.
		 * @function
		 * @param {string} phrase - The item in the dropdown that we are applying the highlight to.
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		highlight (phrase) {
			return phrase.replace(new RegExp('(' + this.value + ')', 'gi'), '<strong>$1</strong>')
		},
		/**
		 * To determine which word is being used as the current prediction. A word can only be used if the input value matches starting at index 0 (case insensitive).
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		calculatePredictiveValue () {
			if (!this.value.length) {
				this.predictiveValue = ''
				return
			}

			// Going to attempt a binary search here
			let updated = false
			let minIndex = 0
			let maxIndex = this.sortedPredictionList.length - 1
			let currentIndex
			let currentElement

			while (minIndex <= maxIndex) {
				currentIndex = (minIndex + maxIndex) / 2 | 0
				if (this.multiSearch) {
					currentElement = this.buildText(this.sortedPredictionList[currentIndex])
				} else if (this.objectKey) {
					currentElement = this.sortedPredictionList[currentIndex][this.objectKey]
				} else {
					currentElement = this.sortedPredictionList[currentIndex]
				}
				if (currentElement.toLowerCase().indexOf(this.value.toLowerCase()) === 0) {
					// Creating this element to handle upper-vs-lower case differences between the predictive name and the typed value
					let frankensteinElement = this.value + currentElement.substring(this.value.length)
					this.predictiveValue = frankensteinElement
					updated = true
					break
				} else if (currentElement.toLowerCase() < this.value.toLowerCase()) {
					minIndex = currentIndex + 1
				} else if (currentElement.toLowerCase() > this.value.toLowerCase()) {
					maxIndex = currentIndex - 1
				}
			}

			if (!updated) {
				// If we couldn't find a word that matches then clear the predictive text.
				this.predictiveValue = ''
			}
		},
		/**
		 * To clear the current predicted word.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.4
		 */
		hidePredictiveValue () {
			this.predictiveValue = ''
		},
		/**
		 * To select the current predictive value and set it as the actual value.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		selectPredictiveOption () {
			this.value = this.predictiveValue

			// This value is used in case the user is hovering over an option which is not the predictive option
			let currentOverride
			for (let i = 0, x = this.items.length; i < x; i++) {
				if (this.multiSearch) {
					if (this.buildText(this.items[i]).toLowerCase() === this.value.toLowerCase()) {
						currentOverride = i
					}
				} else if (this.objectKey) {
					if (this.items[i][this.objectKey].toLowerCase() === this.value.toLowerCase()) {
						currentOverride = i
					}
				} else {
					if (this.items[i].toLowerCase() === this.value.toLowerCase()) {
						currentOverride = i
					}
				}
			}

			this.hit(false, currentOverride)
			this.hidePredictiveValue()
		},
		/**
		 * To remove any items that should be restricted by the passed in filter
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.7
		 * @todo Add logic to handle array of filters
		 * @todo Add logic to handle filters in multi search
		 */
		runFilter () {
			if (this.filter) {
				if (Array.isArray(this.filter)) {
					// Add this logic
				} else {
					for (let i = 0, x = this.items.length; i < x; i++) {
						if (this.multiSearch) {
							// Add this logic
						} else if (this.objectKey) {
							if (this.items[i][this.objectKey] === this.filter[this.objectKey]) {
								this.items.splice(i, 1)
								return
							}
						} else {
							if (this.items[i] === this.filter) {
								this.items.splice(i, 1)
								return
							}
						}
					}
				}
			}
		}
	},
	watch: {
		/**
		 * Watch `passedValue` in case the data is changed from the parent component.
		 * @function
		 * @returns {undefined}
		 * @memberof Typeahead
		 * @version 0.0.7
		 */
		'passedValue' () {
			if (this.multiSearch) {
				if (!isEmpty(this.passedValue)) {
					this.value = this.buildText(this.passedValue)
				}
			} else if (this.objectKey) {
				if (this.passedValue[this.objectKey] !== this.value) {
					this.value = this.passedValue[this.objectKey]
				}
			} else {
				if (this.passedValue !== this.value) {
					this.value = this.passedValue
				}
			}
		}
	}
}
</script>
<style>
.form-group.form-md-line-input .unoapp-typeahead .form-control.prediction-input {
	position: absolute; 
	top: 17px; 
	border-bottom: none; 
	z-index: 0; 
	opacity: 0.5;
}
.form-group.form-md-line-input .unoapp-typeahead .form-control.prediction-output-input {
	z-index: 1; 
	position: relative;
}
</style>


// WEBPACK FOOTER //
// Typeahead.vue?57f55531